import React, { useState, useEffect} from 'react';
import { LinkContainer } from 'react-router-bootstrap';
import { useNavigate } from 'react-router-dom';
import Container from 'react-bootstrap/Container';
import Nav from 'react-bootstrap/Nav';
import Navbar from 'react-bootstrap/Navbar';
import NavDropdown from 'react-bootstrap/NavDropdown';
import CustomSearch from '../CustomSearch';
import { BASE_URL } from '../../utils/constants';
import { handleApiError } from '../../utils/errorHandler';
import Badge from 'react-bootstrap/Badge';
import axios from 'axios';
import { canViewAnalysis, canViewSurgeonTasks, canViewPatientCompliance, canViewPatientComplications, getRoleDisplayName, getRoleBadgeColor } from '../../utils/permissions';
import { useAuth } from '../../context/AuthContext';

import './Header.scss';

const Header = ({ numberOfNotifications, setNumberOfNotifications }) => {
  const { user, isLoggedIn, logout } = useAuth();
  const [surgeonInfo, setSurgeonInfo] = useState(null);
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const searchPatients = async (searchTerm) => {
    const config = {
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
    };
    try {
      const response = await axios.get(`${BASE_URL}/api/v1/patient/search-box?search=${encodeURIComponent(searchTerm)}`, config);
      return response.data.patients;
    }
    catch (error) {
      handleApiError(error, logout);
      return [];
    }
  };

  useEffect(() => {
    // Early return if no logged in user
    if (!isLoggedIn) {
      return;
    }


    // Track if a fetch is in progress to prevent race conditions
    let isFetching = false;

    const getNumberOfNotifications = async () => {
      // Prevent overlapping requests
      if (isFetching) {
        return;
      }

      isFetching = true;
      const config = {
        withCredentials: true, // Send httpOnly cookie with request
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        },
      };
      try {
        // Add timestamp to prevent browser caching
        const timestamp = Date.now();
        let response = await axios.get(`${BASE_URL}/api/v1/self-assessment/outliers?include-archived=false&_t=${timestamp}`, config);
        const surveyOutliers = response.data.surveyOutliers;

        response = await axios.get(`${BASE_URL}/api/v1/self-assessment/terms-and-conditions-rejected/?_t=${timestamp}`, config);
        const termsAndConditionsRejectedSurveys = response.data.termsAndConditionsRejectedSurveys;

        response = await axios.get(`${BASE_URL}/api/v1/self-assessment/unsubscribe-requests?include-archived=false&_t=${timestamp}`, config);
        const optOutRequests = response.data.unsubscribeRequests;

        response = await axios.get(`${BASE_URL}/api/v1/self-assessment/manual-followups?include-resolved=false&_t=${timestamp}`, config);
        const manualFollowups = response.data.manualFollowups;

        const totalNotifications = surveyOutliers.length + termsAndConditionsRejectedSurveys.length + optOutRequests.length + manualFollowups.length;
        setNumberOfNotifications(totalNotifications);
      }
      catch (error) {
        handleApiError(error, logout);
      } finally {
        isFetching = false;
      }
    };

    // Fetch immediately
    getNumberOfNotifications();

    // Set up polling interval to refresh notifications every 3 minutes
    const intervalId = setInterval(() => {
      getNumberOfNotifications();
    }, 180000); // 180 seconds = 3 minutes

    // Cleanup interval on unmount
    return () => {
      clearInterval(intervalId);
    };
  }, [isLoggedIn, logout]);

  // Fetch surgeon info for display in header
  useEffect(() => {
    const getSurgeonInfo = async () => {
      if (user?.surgeon_id) {
        const config = {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        };
        try {
          const response = await axios.get(`${BASE_URL}/api/v1/surgeon/${user.surgeon_id}`, config);
          setSurgeonInfo(response.data.data);
        }
        catch (error) {
          handleApiError(error, logout);
        }
      }
    };
    if (isLoggedIn) {
      getSurgeonInfo();
    }
  }, [isLoggedIn, user?.surgeon_id, user?.token, logout]);

  const handleOnSelect = (patient) => {
    navigate(`/patients/${patient.custom_id}`);
    setExpanded(false);
  };

  return (
    <header className="header">
      <Navbar expand="lg" bg="dark" expanded={expanded}>
      <Container fluid="xxl">
        <Navbar.Brand>
          <LinkContainer to="/dashboard">
                <Nav.Link className="logo">OTOS</Nav.Link>
          </LinkContainer>
        </Navbar.Brand>
        {isLoggedIn ? (
          <>
          <Navbar.Toggle aria-controls="navbarScroll" onClick={() => setExpanded(expanded ? false : 'expanded')}/>
          <Navbar.Collapse id="navbarScroll">
            {/* Mobile badges at top */}
            <div className="mobile-user-info d-lg-none">
              <Badge bg={getRoleBadgeColor(user.role)}>{user.username.toUpperCase()} - {getRoleDisplayName(user.role)}</Badge>
              {surgeonInfo && (
                <Badge bg="info" className="surgeon-badge">
                  <div className="surgeon-name">{surgeonInfo.surgeon_name}</div>
                  <div className="surgeon-site">{surgeonInfo.site}</div>
                </Badge>
              )}
            </div>

            <Nav className="me-auto my-2 my-lg-0 mx-auto" navbarScroll>
              <LinkContainer to="/dashboard">
                  <Nav.Link onClick={() => setExpanded(false)}>DASHBOARD</Nav.Link>
              </LinkContainer>
              <LinkContainer to="/patients">
                  <Nav.Link onClick={() => setExpanded(false)}>PATIENTS</Nav.Link>
              </LinkContainer>
              {canViewAnalysis(user) && (
                <LinkContainer to="/analysis">
                    <Nav.Link onClick={() => setExpanded(false)}>ANALYSIS</Nav.Link>
                </LinkContainer>
              )}
              <LinkContainer to="/notifications">
                  <Nav.Link onClick={() => setExpanded(false)}>NOTIFICATIONS {numberOfNotifications > 0 && (<Badge pill bg="danger">{numberOfNotifications}</Badge>)}</Nav.Link>
              </LinkContainer>
              <NavDropdown data-bs-theme="dark" title={user.username.toUpperCase()} id="basic-nav-dropdown">
              <NavDropdown.ItemText>
                <Badge bg={getRoleBadgeColor(user.role)}>{getRoleDisplayName(user.role)}</Badge>
              </NavDropdown.ItemText>
              {surgeonInfo && (
                <NavDropdown.ItemText>
                  <Badge bg="info" className="surgeon-badge">
                    <div className="surgeon-name">{surgeonInfo.surgeon_name}</div>
                    <div className="surgeon-site">{surgeonInfo.site}</div>
                  </Badge>
                </NavDropdown.ItemText>
              )}
              <NavDropdown.Divider />
              {canViewSurgeonTasks(user) && (
                <LinkContainer to="/surgeon-tasks">
                  <NavDropdown.Item onClick={() => setExpanded(false)}>Surgeon Tasks</NavDropdown.Item>
                </LinkContainer>
              )}
              {canViewSurgeonTasks(user) && (
                <LinkContainer to="/surgeon-settings">
                  <NavDropdown.Item onClick={() => setExpanded(false)}>Email Settings</NavDropdown.Item>
                </LinkContainer>
              )}
              {canViewPatientCompliance(user) && (
                <LinkContainer to="/patient-compliance">
                  <NavDropdown.Item onClick={() => setExpanded(false)}>Patient Compliance</NavDropdown.Item>
                </LinkContainer>
              )}
              {canViewPatientComplications(user) && (
                <LinkContainer to="/patient-complications">
                  <NavDropdown.Item onClick={() => setExpanded(false)}>Patient Complications</NavDropdown.Item>
                </LinkContainer>
              )}
              {(canViewSurgeonTasks(user) || canViewPatientCompliance(user) || canViewPatientComplications(user)) && <NavDropdown.Divider />}
              <NavDropdown.Item onClick={() => logout()}>Sign Out</NavDropdown.Item>
            </NavDropdown>
            </Nav>

            {/* Mobile flat menu items */}
            <div className="mobile-menu-items d-lg-none">
              {canViewSurgeonTasks(user) && (
                <LinkContainer to="/surgeon-tasks">
                  <button onClick={() => setExpanded(false)}>Surgeon Tasks</button>
                </LinkContainer>
              )}
              {canViewSurgeonTasks(user) && (
                <LinkContainer to="/surgeon-settings">
                  <button onClick={() => setExpanded(false)}>Email Settings</button>
                </LinkContainer>
              )}
              {canViewPatientCompliance(user) && (
                <LinkContainer to="/patient-compliance">
                  <button onClick={() => setExpanded(false)}>Patient Compliance</button>
                </LinkContainer>
              )}
              {canViewPatientComplications(user) && (
                <LinkContainer to="/patient-complications">
                  <button onClick={() => setExpanded(false)}>Patient Complications</button>
                </LinkContainer>
              )}
              <button onClick={() => { logout(); setExpanded(false); }}>Sign Out</button>
            </div>

            <div className="search">
              <CustomSearch
                onSearch={searchPatients}
                onSelect={handleOnSelect}
                placeholder="Search"
              />
            </div>
          </Navbar.Collapse>
          </>
        ) : null
        }
      </Container>
    </Navbar>
    </header>
  );
};


export default Header;
